import { useState } from 'react'
import type { CSSProperties } from 'react'
import type { BadgeTone } from './Badge'

export interface AvatarProps {
  name: string
  tone?: BadgeTone
  size?: number
  ring?: boolean
  style?: CSSProperties
  imageUrl?: string | null
}

const TONE_COLOR: Record<string, string> = {
  emerald: 'var(--emerald-600)',
  gold: 'var(--gold-600)',
  turquoise: 'var(--turquoise)',
  terracotta: 'var(--terracotta)',
  indigo: 'var(--indigo)',
  plum: 'var(--plum)',
  danger: 'var(--danger)',
  warning: 'var(--warning)',
  info: 'var(--info)',
}

function initials(name: string): string {
  const parts = name.trim().split(/\s+/).filter(Boolean)
  if (parts.length === 0) return '?'
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase()
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase()
}

export function Avatar({
  name,
  tone = 'emerald',
  size = 36,
  ring,
  style,
  imageUrl,
}: AvatarProps) {
  const [imageFailed, setImageFailed] = useState(false)
  const color = TONE_COLOR[tone] ?? TONE_COLOR.emerald
  const showImage = !!imageUrl && !imageFailed

  return (
    <div
      aria-hidden="true"
      style={{
        width: size,
        height: size,
        borderRadius: '50%',
        background: showImage ? undefined : color,
        color: '#fff',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        overflow: 'hidden',
        fontFamily: 'var(--font-body)',
        fontWeight: 700,
        fontSize: Math.round(size * 0.4),
        flexShrink: 0,
        boxShadow: ring
          ? `0 0 0 2px var(--surface-card), 0 0 0 4px ${color}`
          : undefined,
        ...style,
      }}
    >
      {showImage ? (
        <img
          src={imageUrl}
          alt=""
          width={size}
          height={size}
          style={{ width: '100%', height: '100%', objectFit: 'cover' }}
          onError={() => setImageFailed(true)}
        />
      ) : (
        initials(name)
      )}
    </div>
  )
}

export default Avatar
